<template>
    <div id="office-use">
        <v-card-subtitle class="font-weight-bold pb-0">
            <v-icon :color="subAppColor" left>mdi-account-key</v-icon> Parent Approval
        </v-card-subtitle>
        <v-card-text>
            <v-form ref="parentApprovalForm">
                <v-container fluid>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="getApplicantAge" disabled hide-details
                                label="Applicant Age at Time of Signature Date" placeholder="Not available">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field ref="approvalDate" v-model="getApplicantApprovalDate" disabled hide-details
                                label="Approval Date" placeholder="Not available">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field ref="approvalStatus" v-model="getApplicantApprovalStatus" disabled
                                hide-details label="Approval Status" placeholder="Not available">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" v-if="item.parentApproval && item.parentApproval.adminParentApproval">
                            <v-text-field v-model="item.parentApproval.adminParentApproval.username" disabled
                                label="Approved By" placeholder="Not available">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" v-if="item.parentApproval && item.parentApproval.adminParentApproval">
                            <v-textarea v-model="item.parentApproval.adminParentApproval.comment" disabled hide-details
                                label="Comment(s)" outlined placeholder="Not available">
                            </v-textarea>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
            <v-dialog v-model="approvalDialog" max-width="540">
                <v-card>
                    <v-card-title>
                        Approve Parental Approval
                    </v-card-title>
                    <v-card-text>
                        <v-form ref="approvalForm" v-model="validParentalApproval" lazy-validation>
                            <v-textarea v-model="comments" auto-grow counter="512" outlined label="Comments"
                                placeholder="Please provide any comments, notes or reasoning for this approval" required
                                :rules="commentsRules">
                            </v-textarea>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="parentalApprovalBtn" class="white--text" :color="successColor"
                            :disabled="!validParentalApproval" @click="approve()">
                            Approve
                        </v-btn>
                        <v-btn :color="warningColor" @click="closeApprovalDialog()">
                            Cancel
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn v-if="getAccess() && getApprovalStatus()" class="white--text" :color="successColor"
                @click="openApprovalDialog">
                Approve
            </v-btn>
            <v-btn v-if="getAccess() && getApprovalStatus()" ref="reissueCodeBtn" class="white--text"
                :color="primaryColor" @click="reissueCode">
                Reissue Code
            </v-btn>
        </v-card-actions>
        <v-card-subtitle class="font-weight-bold pb-0">
            <v-icon :color="subAppColor" left>mdi-folder-information</v-icon> Office Use Information
        </v-card-subtitle>
        <v-card-text>
            <v-form ref="officeUseForm">
                <v-container fluid>
                    <v-row>
                        <v-col cols="12">
                            <v-dialog ref="confirmationSentDialog" v-model="confirmationSentDialog"
                                :return-value.sync="item.officeUse.confirmationSent" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="item.officeUse.confirmationSent" :color="primaryColor"
                                        :disabled="item.officeUse.confirmationSent || !getAccess()"
                                        label="Confirmation E-mail Sent" prepend-icon="mdi-email-send"
                                        placeholder="Not specified" readonly v-bind="attrs" v-on="on"></v-text-field>
                                </template>
                                <v-date-picker v-model="item.officeUse.confirmationSent" :color="primaryColor"
                                    scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text :color="primaryColor"
                                        @click="$refs.confirmationSentDialog.save(item.officeUse.confirmationSent)">
                                        OK
                                    </v-btn>
                                    <v-btn text :color="warningColor" @click="confirmationSentDialog = false">
                                        Cancel
                                    </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12">
                            <v-dialog ref="residenceContractSentDialog" v-model="residenceContractSentDialog"
                                :return-value.sync="item.officeUse.residenceContractSent" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="item.officeUse.residenceContractSent" :color="primaryColor"
                                        :disabled="item.officeUse.residenceContractSent || !getAccess()"
                                        label="Residence Contract Sent" prepend-icon="mdi-email-send"
                                        placeholder="Not specified" readonly v-bind="attrs" v-on="on"></v-text-field>
                                </template>
                                <v-date-picker v-model="item.officeUse.residenceContractSent" :color="primaryColor"
                                    scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text :color="primaryColor"
                                        @click="$refs.residenceContractSentDialog.save(item.officeUse.residenceContractSent)">
                                        OK
                                    </v-btn>
                                    <v-btn text :color="warningColor" @click="residenceContractSentDialog = false">
                                        Cancel
                                    </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12">
                            <v-dialog ref="residenceContractReceivedDialog" v-model="residenceContractReceivedDialog"
                                :return-value.sync="item.officeUse.residenceContractReceived" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="item.officeUse.residenceContractReceived"
                                        :color="primaryColor"
                                        :disabled="item.officeUse.residenceContractReceived || !getAccess()"
                                        label="Residence Contract Received" prepend-icon="mdi-email-receive"
                                        placeholder="Not specified" readonly v-bind="attrs" v-on="on"></v-text-field>
                                </template>
                                <v-date-picker v-model="item.officeUse.residenceContractReceived" :color="primaryColor"
                                    scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text :color="primaryColor"
                                        @click="$refs.residenceContractReceivedDialog.save(item.officeUse.residenceContractReceived)">
                                        OK
                                    </v-btn>
                                    <v-btn text :color="warningColor" @click="residenceContractReceivedDialog = false">
                                        Cancel
                                    </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12">
                            <v-dialog ref="singleRoomApplicationReceivedDialog"
                                v-model="singleRoomApplicationReceivedDialog"
                                :return-value.sync="item.officeUse.singleRoomApplicationReceived" persistent
                                width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="item.officeUse.singleRoomApplicationReceived"
                                        :color="primaryColor"
                                        :disabled="item.officeUse.singleRoomApplicationReceived || !getAccess()"
                                        label="Single Room Application Received" prepend-icon="mdi-email-receive"
                                        placeholder="Not specified" readonly v-bind="attrs" v-on="on"></v-text-field>
                                </template>
                                <v-date-picker v-model="item.officeUse.singleRoomApplicationReceived"
                                    :color="primaryColor" scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text :color="primaryColor"
                                        @click="$refs.singleRoomApplicationReceivedDialog.save(item.officeUse.singleRoomApplicationReceived)">
                                        OK
                                    </v-btn>
                                    <v-btn text :color="warningColor"
                                        @click="singleRoomApplicationReceivedDialog = false">
                                        Cancel
                                    </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12">
                            <v-dialog ref="inResidenceDialog" v-model="inResidenceDialog"
                                :return-value.sync="item.officeUse.inResidence" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="item.officeUse.inResidence" :color="primaryColor"
                                        :disabled="item.officeUse.inResidence || !getAccess()" label="In Residence"
                                        prepend-icon="mdi-home-account" placeholder="Not specified" readonly
                                        v-bind="attrs" v-on="on"></v-text-field>
                                </template>
                                <v-date-picker v-model="item.officeUse.inResidence" :color="primaryColor" scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text :color="primaryColor"
                                        @click="$refs.inResidenceDialog.save(item.officeUse.inResidence)">
                                        OK
                                    </v-btn>
                                    <v-btn text :color="warningColor" @click="inResidenceDialog = false">
                                        Cancel
                                    </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field v-model="item.paymentMethod" disabled hide-details label="Payment Method"
                                placeholder="Not available" prepend-icon="mdi-cash">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-dialog ref="paymentReceivedDialog" v-model="paymentReceivedDialog"
                                :return-value.sync="item.officeUse.paymentReceived" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="item.officeUse.paymentReceived" :color="primaryColor"
                                        :disabled="item.officeUse.paymentReceived || !getAccess()"
                                        label="Payment Received" prepend-icon="mdi-cash-refund"
                                        placeholder="Not specified" readonly v-bind="attrs" v-on="on"></v-text-field>
                                </template>
                                <v-date-picker v-model="item.officeUse.paymentReceived" :color="primaryColor"
                                    scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text :color="primaryColor"
                                        @click="$refs.paymentReceivedDialog.save(item.officeUse.paymentReceived)">
                                        OK
                                    </v-btn>
                                    <v-btn text :color="warningColor" @click="paymentReceivedDialog = false">
                                        Cancel
                                    </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12">
                            <v-dialog ref="paymentConfirmedDialog" v-model="paymentConfirmedDialog"
                                :return-value.sync="item.officeUse.paymentConfirmed" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="item.officeUse.paymentConfirmed" :color="primaryColor"
                                        :disabled="item.officeUse.paymentConfirmed || !getAccess()"
                                        label="Payment Confirmed" prepend-icon="mdi-cash-check"
                                        placeholder="Not specified" readonly v-bind="attrs" v-on="on"></v-text-field>
                                </template>
                                <v-date-picker v-model="item.officeUse.paymentConfirmed" :color="primaryColor"
                                    scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text :color="primaryColor"
                                        @click="$refs.paymentConfirmedDialog.save(item.officeUse.paymentConfirmed)">
                                        OK
                                    </v-btn>
                                    <v-btn text :color="warningColor" @click="paymentConfirmedDialog = false">
                                        Cancel
                                    </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12">
                            <v-select v-model="item.officeUse.applicationStatus" :color="primaryColor"
                                :disabled="!getAccess()" :items="applicationStatusItems" label="Application Status"
                                placeholder="Please select...">
                            </v-select>
                        </v-col>
                        <v-col cols="12">
                            <v-checkbox v-model="item.officeUse.printed" :color="primaryColor" dense
                                :disabled="!getAccess()" false-value="0" label="Print" true-value="1">
                            </v-checkbox>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea v-model="item.officeUse.comments" auto-grow :color="primaryColor"
                                :disabled="!getAccess()" hide-details label="Comments" placeholder="No comments..."
                                outlined>
                            </v-textarea>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="540">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-if="getAccess()" class="white--text" :color="successColor" v-bind="attrs" v-on="on">
                        Save
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title>
                        Update Applicant - Office Use
                    </v-card-title>
                    <v-card-text>
                        Please verify and ensure that the Office Use information is correct and accurate. To proceed,
                        please click confirm.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="updateOfficeUseBtn" :color="successColor" text @click="updateApplicant()">
                            Confirm
                        </v-btn>
                        <v-btn :color="warningColor" text @click="closeDialog()">
                            Cancel
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-actions>
    </div>
</template>

<script>
export default {
    name: 'applicant-office-use',
    props: {
        item: {
            required: true
        },
        permission: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            applicationStatusItems: [{
                    text: 'In-process',
                    value: 'In-process'
                },
                {
                    text: ' Waiting list',
                    value: 'Waiting list'
                }, {
                    text: 'Completed',
                    value: 'Completed'
                },
                {
                    text: 'Withdrawn',
                    value: 'Withdrawn'
                }
            ],
            approvalDialog: false,
            comments: '',
            commentsRules: [
                v => !!v || 'Comments is required',
                v => (v && v.length <= 512) || 'Comments must be between 1 and 512 characters',
            ],
            confirmationSentDialog: false,
            dialog: false,
            inResidenceDialog: false,
            paymentConfirmedDialog: false,
            paymentReceivedDialog: false,
            residenceContractSentDialog: false,
            residenceContractReceivedDialog: false,
            singleRoomApplicationReceivedDialog: false,
            validParentalApproval: true
        }
    },
    methods: {
        approve() {
            if (this.$refs.approvalForm.validate()) {
                const that = this;
                const formData = new FormData();
                let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl`;

                // Parse array into string
                formData.append('code', this.item.parentApprovalCode);
                formData.append('comment', this.comments);
                formData.append('mode', 'adminParentApproval');

                // Submit loader
                this.$refs.parentalApprovalBtn.loading = true;

                // Post request
                fetch(api, {
                        method: 'POST',
                        body: formData
                    })
                    .then(function (response) {
                        // Return JSON response
                        return response.json();
                    })
                    .then(function (json) {
                        // Approved
                        if (json.status.match(/^approved/gi)) {
                            that.$emit('setNotification', '#1dc9b7', 'mdi-account-check', `The Parent Approval has been successfully approved.`, true);
                        } else if (json.status.match(/^previously approved/i)) {
                            that.$emit('setNotification', '#ffb822', 'mdi-account-alert', `The Parent Approval has already been approved.`, true);
                        } else if (json.message.match(/UNAUTHORIZED/i)) {
                            that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to perform this action. Please refresh your application.`, true);
                        } else {
                            that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `${json.message}.`, true);
                        }

                        that.$emit('getCurrentApplicant');
                        that.$refs.parentalApprovalBtn.loading = false;
                        that.closeApprovalDialog();
                    })
                    .catch(function () {
                        that.$refs.parentalApprovalBtn.loading = false;
                        that.closeApprovalDialog();
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);

                    });
            }
        },
        closeApprovalDialog() {
            this.approvalDialog = false;
        },
        closeDialog() {
            this.dialog = false;
        },
        getAccess() {
            if (this.permission.enabled == "1" && this.permission.access == "write") {
                return true;
            }

            return false;
        },
        getApprovalStatus() {
            // Check if the student is approved
            if (this.item.parentApproval && this.item.parentApproval.timestamp) {
                return false;
            } else {
                let dateOfBirth = this.item.dateOfBirth || '';
                let signatureDate = this.item.residenceAgreement.applicantSignatureDate || '';

                if (dateOfBirth && signatureDate) {
                    let dob = new Date(dateOfBirth);
                    let sd = new Date(signatureDate);
                    let age = sd.getFullYear() - dob.getFullYear();
                    let month = sd.getMonth() - dob.getMonth();

                    if (month < 0 || (month === 0 && sd.getDate() < dob.getDate())) {
                        age--;
                    }

                    if (age < 18) {
                        return true;
                    }
                }
            }

            return false;
        },
        openApprovalDialog() {
            this.comments = '';

            if (this.$refs.approvalForm) {
                this.$refs.approvalForm.resetValidation();
            }

            this.approvalDialog = true;
        },
        reissueCode() {
            const that = this;
            const formData = new FormData();
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?reissueParentApproval`;

            // Parse array into string
            formData.append('id', this.item.id);
            formData.append('mode', 'reissueParentApproval');

            // Loading button
            this.$refs.reissueCodeBtn.loading = true;

            // Post request
            fetch(api, {
                    method: 'POST',
                    body: formData
                })
                .then(function (response) {
                    // Return JSON response
                    return response.json();
                })
                .then(function (json) {
                    // Success result
                    if (json.result.match(/OK/i)) {
                        that.$emit('setNotification', '#1dc9b7', 'mdi-email-send', `The Parental Approval code has been successfully reissued.`, true);
                    } else if (json.message.match(/UNAUTHORIZED/i)) {
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to perform this action. Please refresh your application.`, true);
                    } else {
                        that.$emit('setNotification', '#ffb822', 'mdi-email-alert', `The Parental Approval code could not be reissued. ${json.message}.`, true);
                    }

                    that.$refs.reissueCodeBtn.loading = false;
                })
                .catch(function () {
                    that.$refs.reissueCodeBtn.loading = false;
                    that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);
                });
        },
        refreshData() {
            this.$emit('refreshData');
        },
        setNotification(color, icon, message, status) {
            this.$emit('setNotification', color, icon, message, status);
        },
        updateApplicant() {
            const that = this;
            const formData = new FormData();
            //let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?updateApplicant`; 

            let api =  `https://${location.hostname}/cgi-bin/res-x-api/api.py`;

            // Parse array into string
            formData.append('data', JSON.stringify({
                id: this.item.id,
                officeUse: this.item.officeUse
            }));
            formData.append('mode', 'adminapplicantset');

            // Loading button
            this.$refs.updateOfficeUseBtn.loading = true;

            // Post request
            fetch(api, {
                    method: 'POST',
                    body: formData
                })
                .then(function (response) {
                    // Return JSON response
                    return response.json();
                })
                .then(function (json) {
                    // Success result
                    if (json.result.match(/OK/i)) {
                        that.$emit('setNotification', '#1dc9b7', 'mdi-account-edit', `The Office Use information has been successfully updated.`, true);
                    } else if (json.message.match(/UNAUTHORIZED/i)) {
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to perform this action. Please refresh your application.`, true);
                    } else {
                        that.$emit('setNotification', '#ffb822', 'mdi-folder-alert', `The Office Use information could not be updated. ${json.message}.`, true);
                    }

                    that.refreshData();
                    that.$refs.updateOfficeUseBtn.loading = false;
                    that.closeDialog();
                })
                .catch(function () {
                    that.refreshData();
                    that.$refs.updateOfficeUseBtn.loading = false;
                    that.closeDialog();
                    that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);
                });
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        getApplicantAge() {
            let dateOfBirth = this.item.dateOfBirth || '';
            let signatureDate = this.item.residenceAgreement.applicantSignatureDate || '';

            if (dateOfBirth && signatureDate) {
                let dob = new Date(dateOfBirth);
                let sd = new Date(signatureDate);
                let age = sd.getFullYear() - dob.getFullYear();
                let month = sd.getMonth() - dob.getMonth();

                if (month < 0 || (month === 0 && sd.getDate() < dob.getDate())) {
                    age--;
                }

                return age;
            }

            return '';
        },
        getApplicantApprovalDate() {
            // Check if the student is approved
            if (this.item.parentApproval && this.item.parentApproval.timestamp) {
                let date = new Date(this.item.parentApproval.timestamp * 1000);

                return date.toDateString();
            }

            return '';
        },
        getApplicantApprovalStatus() {
            // Check if the student is approved
            if (this.item.parentApproval && this.item.parentApproval.timestamp) {
                return `Approved`;
            } else {
                let dateOfBirth = this.item.dateOfBirth || '';
                let signatureDate = this.item.residenceAgreement.applicantSignatureDate || '';

                if (dateOfBirth && signatureDate) {
                    let dob = new Date(dateOfBirth);
                    let sd = new Date(signatureDate);
                    let age = sd.getFullYear() - dob.getFullYear();
                    let month = sd.getMonth() - dob.getMonth();

                    if (month < 0 || (month === 0 && sd.getDate() < dob.getDate())) {
                        age--;
                    }

                    if (age >= 18) {
                        return `Not required`;
                    } else {
                        return `Not approved`;
                    }
                }
            }

            return '';
        },
        primaryColor() {
            return '#5867dd';
        },
        subAppColor() {
            return '#FFA800';
        },
        successColor() {
            return '#1dc9b7';
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>

<style scoped>
.v-input--checkbox {
    margin-top: 0;
}
</style>